<script setup lang="ts">
import { computed, useAttrs } from 'vue';

defineOptions({ inheritAttrs: false, name: 'EGS_Checkbox' });
type CheckboxValue = string | number | boolean | Record<string, unknown> | null;
const props = withDefaults(defineProps<{ modelValue?: CheckboxValue | CheckboxValue[]; value?: CheckboxValue; binary?: boolean; disabled?: boolean; indeterminate?: boolean; inputId?: string }>(), {
	modelValue: false,
	value: true,
	binary: false,
	disabled: false,
	indeterminate: false
});
const emit = defineEmits<{ 'update:modelValue': [value: unknown] }>();
const attrs = useAttrs();
const checked = computed(() => (props.binary ? Boolean(props.modelValue) : Array.isArray(props.modelValue) && props.modelValue.includes(props.value)));
const inputIdentity = computed(() => (props.inputId ? { id: props.inputId } : {}));
function change(event: Event) {
	const isChecked = (event.target as HTMLInputElement).checked;
	if (props.binary) emit('update:modelValue', isChecked);
	else {
		const values = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
		const index = values.indexOf(props.value);
		if (isChecked && index < 0) values.push(props.value);
		if (!isChecked && index >= 0) values.splice(index, 1);
		emit('update:modelValue', values);
	}
}
</script>

<template>
	<label :class="['relative inline-flex h-5 w-5 shrink-0 align-middle', disabled ? 'opacity-60' : '', attrs['class']]">
		<input
			v-bind="{ ...attrs, ...inputIdentity }"
			type="checkbox"
			class="peer absolute inset-0 z-10 m-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
			:checked="checked"
			:disabled="disabled"
			:aria-checked="indeterminate ? 'mixed' : checked"
			@change="change"
		/>
		<span
			:class="[
				'flex h-5 w-5 items-center justify-center rounded border transition peer-focus-visible:ring-2 peer-focus-visible:ring-primary-300',
				checked || indeterminate ? 'border-primary-600 bg-primary-600 text-white' : 'border-surface bg-[var(--egs-content-background)] text-transparent'
			]"
		>
			<font-awesome-icon :icon="indeterminate ? 'minus' : 'check'" class="h-3 w-3" />
		</span>
	</label>
</template>
